import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import {
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '~/components/isolated/Carousel';
import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import ProductCardS from '~/components/ProductCardV3/variants/ProductCardS/ProductCardS';
import {
  ProductListAnalyticsProvider,
  useCategoryBlockProductList,
} from '~/components/ProductListAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { CarouselBackground } from './CategoryBlockBackgroundImage';
import { createCategoryUrl } from './utils';

const containerClass = 'mx-auto w-full max-w-(--layout-max-width)';
const slideClass =
  'basis-[120px]! shrink-0 grow-0 pl-0! [&:not(:last-child)]:mr-4 [&>div]:w-full md:basis-[calc(1/6*100%-16px+calc(16px/6))]!';
const itemClass = 'w-[120px] shrink-0 md:w-[227px]';
const carouselContentClass = 'pb-2 -mx-4 px-4 w-[calc(100%+32px)]!';
const skeletonContainerClass =
  'flex gap-4 [&>*]:w-[120px] [&>*]:shrink-0 md:[&>*]:w-[227px]';

export interface ProductsCarouselWithBackgroundProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
  /** Ratio of the box the mobile branch reserves. */
  mobileAspectRatio: number;
  /** Ratio of the box the desktop branch reserves. */
  desktopAspectRatio: number;
  /** Box classes for the mobile carousel root. */
  mobileClassName: string;
  /** Box classes for the desktop carousel element. */
  carouselClassName: string;
  priority?: boolean;
  intro?: ReactNode;
  mobileImage?: string;
}

/**
 * A products carousel whose box carries a full-bleed category background.
 *
 * Shared by the blocks that differ only in the shape of that box: the ratios
 * they reserve and the classes that reserve them. Everything else — the slide
 * geometry, the skeleton, the view-all wiring — was duplicated line for line
 * across them, so a fix to the background wiring had to be made twice.
 */
export const ProductsCarouselWithBackground: React.FC<
  ProductsCarouselWithBackgroundProps
> = ({
  category,
  mobileAspectRatio,
  desktopAspectRatio,
  mobileClassName,
  carouselClassName,
  priority = false,
  intro,
  mobileImage,
  className,
  ...props
}) => {
  const { pathname } = useLocation();
  const { isMobile } = useGlobal();
  const blockList = useCategoryBlockProductList(category, 'carousel');

  // Hide the block when there are no products and none are on the way —
  // home-page categories get their products with the categories tree, so
  // productsRequest may never run and never become fulfilled.
  if (!category.products.length && !category.productsRequest.isLoading) {
    return null;
  }

  const categoryId = category?.subcategory[0]?.id;

  if (!category.subcategory.length || !categoryId) {
    return null;
  }

  const linkTo =
    pathname !== '/search' ? createCategoryUrl({ id: categoryId }) : undefined;

  const products = category.products.slice(0, isMobile ? 10 : 15);
  const items = products.map((product, index) => ({
    id: product.id,
    node: (
      <ProductCardS
        product={product}
        index={index}
        transparent
        className="w-full!"
      />
    ),
  }));

  const skeletonComponent = (
    <div className={skeletonContainerClass}>
      {Array.from({ length: 5 }, (_, i) => (
        <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="s" />
      ))}
    </div>
  );

  const commonProps = {
    linkTo,
    title: category.name,
    categoryId,
    items,
    skeletonComponent: items.length === 0 ? skeletonComponent : undefined,
  };

  const backgroundSrc =
    isMobile && mobileImage ? mobileImage : category.previewImage;

  const background = backgroundSrc ? (
    <CarouselBackground
      src={backgroundSrc}
      isMobile={isMobile}
      mobileAspectRatio={mobileAspectRatio}
      desktopAspectRatio={desktopAspectRatio}
      priority={priority}
    />
  ) : undefined;

  return (
    <ProductListAnalyticsProvider list={blockList}>
      <div {...props} className={cn(containerClass, className)}>
        {isMobile ? (
          <ProductCarouselMobile
            {...commonProps}
            title={intro ? undefined : commonProps.title}
            showViewAllOnMobile
            viewAllVariant="bottom"
            itemClassName={itemClass}
            background={background}
            intro={intro}
            className={mobileClassName}
          />
        ) : (
          <ProductCarouselDesktop
            {...commonProps}
            navigationVariant="header"
            slideClassName={slideClass}
            contentClassName={carouselContentClass}
            background={background}
            intro={intro}
            carouselClassName={carouselClassName}
          />
        )}
      </div>
    </ProductListAnalyticsProvider>
  );
};
